if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get productId() {
        return this.getAttribute("data-product-id");
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector("quantity-input input");
      }

      lockScroll() {
        const scrollY = window.scrollY;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        document.body.style.overflow = "hidden";
        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`;
        }

        return scrollY;
      }

      unlockScroll(scrollY) {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
        // Skip scroll restoration when variant change is driven by the sticky ATC bar;
        // the sticky bar handles its own scroll-position preservation.
        if (!window.__stickyAtcSyncing) {
          window.scrollTo(0, scrollY);
        }
      }

      updateAddonItems(html) {
        // Find the addon items block in both current and new HTML
        const currentAddonBlock = this.querySelector(".addon-items");
        const newAddonBlock = html.querySelector(".addon-items");

        if (currentAddonBlock && newAddonBlock) {
          // Use HTMLUpdateUtility to transition the addon items block
          HTMLUpdateUtility.viewTransition(currentAddonBlock, newAddonBlock, this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);
        } else if (currentAddonBlock && !newAddonBlock) {
          // Hide the addon block if new variant doesn't have addon items
          currentAddonBlock.style.display = "none";
          currentAddonBlock.classList.remove("abled");
        } else if (!currentAddonBlock && newAddonBlock) {
          // If there's a new addon block but no current one, we need to add it
          // This might require inserting it in the right place in the DOM
          const productBlocks = this.querySelector(".product__blocks");
          if (productBlocks) {
            productBlocks.appendChild(newAddonBlock.cloneNode(true));
          }
        }
      }

      updateColorSwatches(html) {
        // Find the color picker blocks in both current and new HTML
        const currentColorBlock = this.querySelector(".color_picker_custom");
        const newColorBlock = html.querySelector(".color_picker_custom");

        if (currentColorBlock && newColorBlock) {
          // Use HTMLUpdateUtility to transition the color picker block
          HTMLUpdateUtility.viewTransition(
            currentColorBlock, 
            newColorBlock, 
            this.preProcessHtmlCallbacks, 
            this.postProcessHtmlCallbacks
          );
          
          // Re-initialize the toggle button functionality after update
          setTimeout(() => {
            const toggleBtn = document.getElementById('toggle-swatch-btn');
            if (toggleBtn) {
              // Reset button state
              toggleBtn.setAttribute('data-expanded', 'false');
              toggleBtn.innerText = 'View More';
              
              // Ensure proper initial display state for B2B customers
              const swatches = document.querySelectorAll('.product__info-wrapper .custom_product_swatch_product .swatch_button');
              swatches.forEach((swatch, index) => {
                if (index >= 17) {
                  swatch.style.display = 'none';
                }
              });
            }
          }, 100);
        } else if (currentColorBlock && !newColorBlock) {
          // Hide the color block if new variant doesn't have color swatches
          currentColorBlock.style.display = "none";
        } else if (!currentColorBlock && newColorBlock) {
          // If there's a new color block but no current one, add it
          const productBlocks = this.querySelector(".product__blocks");
          if (productBlocks) {
            productBlocks.appendChild(newColorBlock.cloneNode(true));
          }
        }
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, this.handleOptionValueChange.bind(this));

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === "true" && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || "main-product";

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode) : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector("#MainProduct-quick-view__content");
          if (quickView && viewMode === "quick-view") {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML = html.querySelector("head title").innerHTML;
            HTMLUpdateUtility.viewTransition(document.querySelector("main"), html.querySelector("main"), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);
            HTMLUpdateUtility.viewTransition(document.getElementById("shopify-section-sticky-atc-bar"), html.getElementById("shopify-section-sticky-atc-bar"), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);
          } else {
            HTMLUpdateUtility.viewTransition(this, html.querySelector("product-info"), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector("#MainProduct-quick-view__content");
          if (quickView && viewMode === "quick-view") {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);
          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };
          // custom code

          const targetSelector = `.variant_base_images[data-varianttitle="${variant.option1.toLowerCase()}"]`;
          const targetElements = document.querySelectorAll(targetSelector);

          if (targetElements.length > 0) {
            // Skip body-overflow lock/unlock when variant change is driven by the
            // sticky ATC bar — it manages its own scroll-position preservation and
            // the overflow toggle causes unnecessary layout shifts.
            const isStickySync = !!window.__stickyAtcSyncing;
            const scrollY = isStickySync ? 0 : this.lockScroll();

            // Hide and remove 'thisactive' from all
            document.querySelectorAll(".variant_base_images").forEach((el) => {
              el.style.display = "none";
              el.classList.remove("thisactive");
            });

            // Show and add 'thisactive' to matched elements
            targetElements.forEach((el) => {
              el.style.display = ""; // or 'block' depending on your layout
              el.classList.add("thisactive");

              // Wait for DOM update before changing active thumb
              setTimeout(() => {
                // Remove active class from all thumbs
                document.querySelectorAll(".product__thumbs-item").forEach((thumb) => thumb.classList.remove("swiper-slide-thumb-active"));

                document.querySelectorAll(".product__media-item").forEach((thumb) => thumb.classList.remove(".swiper-slide-active"));
                // Add active class to first thumb in this gallery
                const firstThumb = el.querySelector(".product__media-gallery-thumbails .product__thumbs-item");
                const firstMedia = el.querySelector(".product__media-gallery-viewer .product__media-item");
                if (firstMedia) {
                  firstThumb.classList.add("swiper-slide-active");
                }
                if (firstThumb) {
                  firstThumb.classList.add("swiper-slide-thumb-active");
                }
                if (!isStickySync) {
                  this.unlockScroll(scrollY);
                }
              }, 500);
            });
          }
          // end custom code

          //updateAddonItems Re-render function
          this.updateAddonItems(html);
          this.updateColorSwatches(html);

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));
          updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");
          updateSourceFromDestination("Badges", ({ classList }) => classList.contains("hidden"));
          updateSourceFromDestination("PricePerItem", ({ classList }) => classList.contains("hidden"));
          updateSourceFromDestination("Volume");

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove("hidden");
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove("hidden");

          HTMLUpdateUtility.viewTransition(document.querySelector(`#SizeChart-${this.sectionId}`), html.querySelector(`#SizeChart-${this.sectionId}`), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(addButtonUpdated ? addButtonUpdated.hasAttribute("disabled") : true, FoxTheme.variantStrings.soldOut);

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(
            new CustomEvent("variant:changed", {
              detail: {
                variant: variant,
              },
            })
          );
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector("variant-selects [data-selected-variant]")?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, "text/html");
            callback(html);
            if (reRenderingBundel && typeof reRenderingBundel == "function") {
              reRenderingBundel();
            }
          })
          .then(() => {
            // set focus to last clicked option value
            // Use preventScroll to avoid scrolling to the PDP section when variant
            // change is initiated from the sticky ATC bar (element is off-screen).
            document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState({}, "", `${url}${variantId ? `?variant=${variantId}` : ""}`);
      }

      updateVariantInputs(variantId) {
        document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === "function") {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute("disabled", "disabled");
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute("disabled");
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        const button = document.getElementById(`product-form-${this.dataset.section}`);
        const addButton = button.querySelector('[name="add"]');
        const addButtonText = button.querySelector('[name="add"] > span');
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (!addButton) return;
        addButtonText.textContent = FoxTheme.variantStrings.unavailable;
        if (price) price.classList.add("hidden");
        if (inventory) inventory.classList.add("hidden");
        if (sku) sku.classList.add("hidden");
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute("data-original-section")) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(".quantity__rules-cart")?.classList.add("btn--loading");

        fetch(`${this.getAttribute("data-url")}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, "text/html");
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector(".quantity__rules-cart")?.classList.remove("btn--loading");
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}